import { useCallback, useEffect, useState } from 'react';
import type { Call, CallStatus } from '@supporchat/shared';
import { api } from '../lib/api';
import { formatPhone } from '../components/CallBar';

/**
 * Histórico de ligações da empresa.
 *
 * O registro já existia no banco desde as chamadas de voz, costurado entre o que
 * o navegador sabe (quem atendeu, quanto durou) e o que o webhook sabe (a ligação
 * que ninguém pegou) — mas não havia tela nenhuma mostrando isso, então a
 * chamada perdida só existia para quem olhasse o banco.
 */
export function CallsPage({ onOpenConversation }: { onOpenConversation?: (id: string) => void }) {
  const [calls, setCalls] = useState<Call[]>([]);
  const [filter, setFilter] = useState<'all' | 'missed'>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setCalls(await api.listCalls());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = filter === 'missed' ? calls.filter((call) => call.status === 'missed') : calls;
  const missed = calls.filter((call) => call.status === 'missed').length;

  return (
    <div className="page">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h3 className="card__title">Ligações</h3>
          <button className="btn btn--sm" style={{ marginLeft: 'auto' }} onClick={() => void load()}>
            Atualizar
          </button>
        </div>

        <div className="conversation-list__filters" style={{ border: 0, padding: '0 0 12px' }}>
          <button
            className="filter-chip"
            aria-pressed={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            Todas ({calls.length})
          </button>
          <button
            className="filter-chip"
            aria-pressed={filter === 'missed'}
            onClick={() => setFilter('missed')}
          >
            Perdidas ({missed})
          </button>
        </div>

        {error && <div className="alert alert--error">{error}</div>}

        {loading ? (
          <p className="muted">Carregando...</p>
        ) : shown.length === 0 ? (
          <p className="muted">
            {filter === 'missed' ? 'Nenhuma chamada perdida.' : 'Nenhuma ligação registrada ainda.'}
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Cliente</th>
                <th>Direção</th>
                <th>Situação</th>
                <th>Duração</th>
                <th>Atendida por</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((call) => (
                <tr key={call.id} className={call.status === 'missed' ? 'table__row--alert' : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(call.startedAt)}</td>
                  <td>
                    <div>{call.peerName ?? formatPhone(call.peerPhone)}</div>
                    {call.peerName && (
                      // Monoespaçado como todo dado que se disca ou se dita.
                      <div className="muted mono">{formatPhone(call.peerPhone)}</div>
                    )}
                  </td>
                  <td>{call.direction === 'inbound' ? '↓ recebida' : '↑ realizada'}</td>
                  <td>
                    <span className={`badge ${BADGE[call.status]}`}>{STATUS_LABEL[call.status]}</span>
                  </td>
                  <td className="mono-cell">
                    {call.durationSeconds > 0 ? formatDuration(call.durationSeconds) : '—'}
                  </td>
                  <td>{call.answeredByName ?? <span className="muted">—</span>}</td>
                  <td>
                    {call.conversationId && onOpenConversation && (
                      <button
                        className="btn btn--sm"
                        onClick={() => onOpenConversation(call.conversationId!)}
                      >
                        Abrir conversa
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="field__hint" style={{ marginTop: 12 }}>
          Chamada que ninguém atendeu só aparece aqui se o webhook do Wavoip estiver cadastrado — é ele
          que reporta o que o navegador fechado não viu.
        </p>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<CallStatus, string> = {
  ringing: 'tocando',
  active: 'em curso',
  ended: 'atendida',
  missed: 'perdida',
  rejected: 'recusada',
  failed: 'falhou',
};

/*
 * Vermelho é falha de verdade — a ligação que nem chegou a acontecer. Cinza é
 * desfecho sem problema técnico (o cliente recusou), âmbar é o que pede retorno
 * (ninguém atendeu). Antes falha e recusa dividiam o mesmo cinza, e a linha que
 * merecia investigação passava como rotina.
 */
const BADGE: Record<CallStatus, string> = {
  ringing: 'badge--pending',
  active: 'badge--bot',
  ended: 'badge--human',
  missed: 'badge--pending',
  rejected: 'badge--closed',
  failed: 'badge--failed',
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}min ${String(seconds % 60).padStart(2, '0')}s`;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const hoje = new Date();
  const mesmoDia = date.toDateString() === hoje.toDateString();

  return mesmoDia
    ? `hoje ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    : date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}
